import type { CSSProperties, ReactNode } from 'react';
import { Avatar, Badge, Button, Card, Typography } from '../ui';
import { SymphonyMark } from '../../SymphonyMark';
import { WhatsAppMarkGlyph } from '../connectors/BrandGlyphs';
import { useTranslation } from '../../../lib/i18n';
import { useProductName } from '../../../lib/use-brand';
import type { PublicProfileCard } from '../../../lib/profile-link-card';
import { symphonyQrDesign } from '../../../lib/qr-design';
import {
  effectiveCard,
  initialsFor,
  ownerFirstName,
  PRIVACY_URL,
  profileAppUrl,
  profileCtaPlan,
  profileDisplayUrl,
  profilePagePalette,
  profileGetYourOwnUrl,
  profilePublicUrl,
  reportUrl,
  SYMPHONY_WEB_URL,
  TERMS_URL,
  whatsAppContactUrl,
  type ProfileCta,
  type ProfileDevice,
} from '../../../lib/profile-link-view';
import { savePendingProfileThread, profileThreadWebUrl } from '../../../lib/profile-thread-target';
import { DesignedQr } from './DesignedQr';
import styles from './ProfileLanding.module.css';

/**
 * The public profile page body — one fixed Symphony template the owner colours in (design §5.1, §5.2).
 *
 * Fixed: the frame, the mark, the two CTAs with their wording and device ordering, the disclosure, the
 * footer, the QR style. The owner's: the palette (as custom properties, already normalized to `#rrggbb` and
 * contrast-adjusted), the headline, bio, topics and the context note — all rendered as TEXT, never HTML.
 *
 * No chat box, no free text, no form (D1): every way forward is a link to WhatsApp, the app, or the web app.
 */
export interface ProfileLandingProps {
  card: PublicProfileCard;
  device: ProfileDevice;
  /** Only true once something actually knows the app is installed; see `profileCtaPlan`. */
  appLikely?: boolean;
  /**
   * The card section on the owner's own website (`/me/<h>[/<c>]?view=embed`): the phone column whatever the
   * device (the frame is a column), the reader's own CTA order, every link in a new tab so the host page stays,
   * and no footer — the page around it is the owner's.
   */
  embed?: boolean;
  /**
   * Called on a CTA tap, before the browser follows the link: the public page counts the tap and fires clickCta.
   * Absent on the embed and on the owner screens' previews, which are not visits.
   */
  onCtaTap?: (cta: ProfileCta) => void;
}

export function ProfileLanding({ card: rawCard, device, appLikely = false, embed = false, onCtaTap }: ProfileLandingProps) {
  const { t } = useTranslation();
  const productName = useProductName();
  const card = effectiveCard(rawCard);
  const palette = profilePagePalette(card.palette);
  const plan = profileCtaPlan({ device, appLikely });
  const name = card.displayName;
  const firstName = ownerFirstName(card.displayName);
  const publicUrl = profilePublicUrl(card.handle, card.contextSlug);
  const waPrefill = String(t('profileLink.wa.prefill', { name: firstName, code: card.waShortCode }));
  const waUrl = whatsAppContactUrl(waPrefill);
  const asks = card.suggestedAsks ?? [];
  const greeting = card.greeting?.trim();
  const appUrl = profileAppUrl(card);

  const vars = {
    '--profile-accent': palette.accent,
    '--profile-bg': palette.bg,
    '--profile-fg': palette.fg,
    '--profile-muted': palette.muted,
  } as CSSProperties;

  const cta = (kind: ProfileCta, primary: boolean): ReactNode => {
    const variant = primary ? 'contained' : 'outlined';
    // A new tab on the desktop (WhatsApp is WhatsApp Web and the reader keeps this page) and in an embed (the
    // page around the frame is the owner's website).
    const newTab = embed || plan.layout === 'desktop' ? { target: '_blank', rel: 'noopener noreferrer' } : {};
    if (kind === 'whatsapp') {
      return (
        <Button
          key={kind}
          href={waUrl}
          external
          variant={variant}
          size="large"
          fullWidth
          startIcon={<WhatsAppMarkGlyph size={20} />}
          onClick={onCtaTap ? () => onCtaTap('whatsapp') : undefined}
          {...newTab}
        >
          {t('profileLink.cta.whatsapp')}
        </Button>
      );
    }
    if (kind === 'app') {
      const sub = plan.layout === 'phone' && plan.appSub === 'open' ? t('profileLink.cta.appOpens') : t('profileLink.cta.appInstall');
      return (
        <Button
          key={kind}
          href={appUrl}
          external
          variant={variant}
          size="large"
          fullWidth
          startIcon={<SymphonyMark size={20} />}
          onClick={onCtaTap ? () => onCtaTap('app') : undefined}
          {...newTab}
        >
          <span className={styles.ctaStack}>
            <span>{t('profileLink.cta.app', { productName })}</span>
            <span className={styles.ctaSub}>{sub}</span>
          </span>
        </Button>
      );
    }
    // Continue on the web opens the profile thread (design §6.1). A visitor with no session is bounced to
    // sign-in by the server and returns to it; the pending record covers the sign-up paths that land
    // elsewhere (lib/profile-thread-target.ts).
    const route = { handle: card.handle, context: card.contextSlug };
    return (
      <Button
        key={kind}
        href={profileThreadWebUrl(route)}
        external
        variant={variant}
        size="large"
        fullWidth
        // In an embed the storage is the host site's partition, not the visitor's Symphony one: nothing to save.
        onClick={
          embed
            ? undefined
            : () => {
                onCtaTap?.('web');
                savePendingProfileThread(route, Date.now());
              }
        }
        {...newTab}
      >
        {t('profileLink.cta.web')}
      </Button>
    );
  };

  const hasStory = Boolean(card.headline || card.bio || card.topics.length);

  // One card holds the whole profile: who they are, what they do, what to ask about. An owner who wrote
  // nothing yet still gets a full card (initial, name, handle), never a sliver of colour over empty space.
  const hero = (
    <Card variant="outline" disablePadding className={styles.hero}>
      {/* `data-field` names the part of the card each owner field draws: the Edit dialog's preview marks the
          one being edited (`PREVIEW_FIELD`). Inert on the public page. */}
      <div className={styles.band} data-field="band">
        {card.contextTitle ? (
          <Badge className={styles.contextBadge} dir="auto" data-field="context">
            {card.contextTitle}
          </Badge>
        ) : null}
      </div>
      <div className={styles.heroBody} data-story={hasStory ? 'full' : 'empty'}>
        <Avatar initials={initialsFor(name)} size={88} className={styles.avatar} />
        <Typography variant="h1" component="h1" align="center" dir="auto" className={styles.name}>
          {name}
        </Typography>
        <Typography variant="p2" color="secondary" align="center" dir="ltr" className={styles.handle}>
          @{card.handle}
        </Typography>
        {card.headline ? (
          <Typography variant="p1" align="center" className={styles.headline} dir="auto" data-field="headline">
            {card.headline}
          </Typography>
        ) : null}
        {card.contextTitle ? (
          <Typography variant="tiny-semi-bold" color="secondary" component="p" align="center" className={styles.contextLabel} dir="auto">
            {t('profileLink.fromContext', { event: card.contextTitle })}
          </Typography>
        ) : null}
        {card.bio ? (
          <Typography variant="p2" component="p" dir="auto" className={styles.bio} data-field="bio">
            {card.bio}
          </Typography>
        ) : null}
        {card.topics.length ? (
          <section className={styles.topics} aria-label={t('profileLink.askMeAbout')} data-field="topics">
            <Typography variant="tiny-semi-bold" component="h2" className={styles.label}>
              {t('profileLink.askMeAbout')}
            </Typography>
            <ul className={styles.chips}>
              {card.topics.map((topic, i) => (
                // By position: an owner can list the same topic twice, and a duplicate key drops a chip.
                // eslint-disable-next-line react/no-array-index-key
                <li key={i} className={styles.chip} dir="auto">
                  {topic}
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        {greeting ? (
          <Typography variant="p2" component="p" dir="auto" className={styles.greeting} data-field="greeting">
            {greeting}
          </Typography>
        ) : null}
        {asks.length ? (
          <section className={styles.topics} aria-label={t('profileLink.tryAsking')} data-field="suggestedAsks">
            <Typography variant="tiny-semi-bold" component="h2" className={styles.label}>
              {t('profileLink.tryAsking')}
            </Typography>
            <ul className={styles.asks}>
              {asks.map((ask, i) => (
                // By position, as the topics: an owner can list the same question twice.
                // eslint-disable-next-line react/no-array-index-key
                <li key={i}>
                  {/* Tap-to-start: the same WhatsApp message path as the button, with the question after the
                      short code — the parser takes the LAST bracketed code, so text after it is the visitor's. */}
                  <a
                    className={styles.ask}
                    dir="auto"
                    href={whatsAppContactUrl(`${waPrefill} ${ask}`)}
                    onClick={onCtaTap ? () => onCtaTap('whatsapp') : undefined}
                    {...(embed || plan.layout === 'desktop' ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  >
                    {ask}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </Card>
  );

  const ctas = (
    <div className={styles.ctas} data-ctas={plan.ctas.join(',')}>
      {plan.ctas.map((kind, i) => cta(kind, i === 0))}
    </div>
  );

  // The legal disclosure keeps its words and its order; it is quieter type, not less of it.
  const disclosure = (
    <p className={styles.disclosure}>
      {t('profileLink.disclosure.assistant', { name: firstName })} <strong>{t('profileLink.disclosure.sees', { name: firstName })}</strong>{' '}
      {t('profileLink.disclosure.noAnonymous')}
    </p>
  );

  // One line on what this is, for a reader who has never heard of it — under the disclosure, quieter still.
  const about = <p className={styles.about}>{t('profileLink.whatIsProduct', { productName })}</p>;

  const footer = (
    <footer className={styles.footer}>
      <a className={styles.getYourOwn} href={profileGetYourOwnUrl(card.handle)}>
        <SymphonyMark size={22} />
        <span>{t('profileLink.footer.getYourOwn')}</span>
      </a>
      <nav className={styles.footerLinks}>
        <a href={reportUrl(card.handle)}>{t('profileLink.footer.report')}</a>
        <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
          {t('profileLink.footer.privacy')}
        </a>
        <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
          {t('profileLink.footer.terms')}
        </a>
      </nav>
    </footer>
  );

  if (embed) {
    return (
      <main className={`${styles.page} ${styles.embed}`} style={vars} data-layout="embed">
        <div className={`${styles.shell} ${styles.main}`}>
          {hero}
          {ctas}
          {disclosure}
          {about}
        </div>
      </main>
    );
  }

  if (plan.layout === 'desktop') {
    return (
      <main className={styles.page} style={vars} data-layout="desktop">
        <div className={`${styles.shell} ${styles.desktop}`}>
          <div className={styles.main}>
            {hero}
            {disclosure}
            {about}
          </div>
          <aside className={styles.side}>
            <Card variant="outline" className={styles.panel}>
              <Typography variant="h4" component="h2" align="center">
                {t('profileLink.scan.title')}
              </Typography>
              <DesignedQr
                design={symphonyQrDesign(publicUrl)}
                caption={profileDisplayUrl(card.handle, card.contextSlug)}
                label={t('profileLink.qr.label', { name, productName })}
                size={200}
              />
              <div className={styles.or}>{t('profileLink.or')}</div>
              {ctas}
              <p className={styles.note}>{t('profileLink.whatsappWebNote')}</p>
            </Card>
          </aside>
        </div>
        {footer}
      </main>
    );
  }

  return (
    <main className={styles.page} style={vars} data-layout="phone">
      <div className={`${styles.shell} ${styles.main}`}>
        {hero}
        {ctas}
        <p className={styles.note}>{t('profileLink.noWhatsapp', { productName })}</p>
        {disclosure}
        {about}
      </div>
      {footer}
    </main>
  );
}

/**
 * Unknown, draft, moderated or deleted — ONE neutral page (§4.5). A draft main profile is byte-identical to
 * a handle nobody holds, so the page is not an oracle for which handles exist.
 */
export function ProfileUnavailable() {
  const { t } = useTranslation();
  const productName = useProductName();
  return (
    <main className={`${styles.page} ${styles.unavailablePage}`}>
      <div className={styles.unavailable}>
        <SymphonyMark size={40} />
        <Typography variant="h2" component="h1" align="center">
          {t('profileLink.unavailable.title')}
        </Typography>
        <Typography variant="p2" color="secondary" align="center">
          {t('profileLink.unavailable.body')}
        </Typography>
        <Button href={SYMPHONY_WEB_URL} external size="large">
          {t('profileLink.unavailable.cta', { productName })}
        </Button>
      </div>
    </main>
  );
}
